import { useEffect, useState } from "react";
import { CreditCard, Wallet, Loader2, TicketPercent } from "lucide-react";
import api from "@/lib/api";
import { money } from "@/lib/format";

// Lets a logged-in member choose to pay a deposit now (balance settled at their
// visit in the POS) or pay the full amount. `item` needs { title, price, deposit_pct }.
// `quoteRef` ({booking_id} or {registration_id}) enables coupon codes + automatic
// membership/promotion discounts, quoted server-side.
export default function PaymentChoice({ item, onDeposit, onFull, quoteRef }) {
  const [busy, setBusy] = useState("");
  const [code, setCode] = useState("");
  const [applied, setApplied] = useState("");
  const [quote, setQuote] = useState(null);
  const [checking, setChecking] = useState(false);
  const basePrice = quote?.original ?? Number(item?.price || 0);

  const fetchQuote = async (c) => {
    if (!quoteRef) return;
    setChecking(true);
    try {
      const { data } = await api.post("/discounts/quote", { ...quoteRef, code: c });
      setQuote(data);
      if (c && data.coupon_valid) setApplied(c);
      else if (c) setApplied("");
    } catch { setQuote(null); }
    finally { setChecking(false); }
  };

  useEffect(() => { fetchQuote(""); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const price = quote && quote.discount > 0 ? Number(quote.final) : basePrice;
  const pct = Number(item?.deposit_pct) > 0 ? Number(item.deposit_pct) : 25;
  const deposit = Math.round(price * pct) / 100;
  const balance = Math.round((price - deposit) * 100) / 100;

  const go = async (which, fn) => {
    setBusy(which);
    try { await fn(applied); } finally { setBusy(""); }
  };

  return (
    <div className="py-4" data-testid="payment-choice">
      <h3 className="font-serif text-2xl text-[#1A242B] text-center">How would you like to pay?</h3>
      <p className="text-sm text-[#4A5D6B] text-center mt-1">
        {item?.title} ·{" "}
        {quote && quote.discount > 0
          ? (<><span className="line-through text-[#8FA89B]">{money(basePrice)}</span> <span className="font-semibold text-[#5C8A72]">{money(price)}</span></>)
          : <>{money(basePrice)}</>}
      </p>
      {quote && quote.discount > 0 && (
        <p className="text-xs text-[#5C8A72] text-center mt-1" data-testid="payment-discount-label">{quote.label} — you save ${Number(quote.discount).toFixed(2)}</p>
      )}
      {quote?.message && <p className="text-xs text-[#B5794E] text-center mt-1" data-testid="payment-discount-message">{quote.message}</p>}

      {quoteRef && (
        <div className="mt-4 flex gap-2 items-center justify-center">
          <div className="relative">
            <TicketPercent size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8FA89B]" />
            <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} data-testid="payment-coupon-input"
              placeholder="Coupon code" className="rounded-full border border-[#E5E0D8] pl-8 pr-3 py-2 text-sm w-44 uppercase font-mono focus:outline-none focus:border-[#144F75]" />
          </div>
          <button onClick={() => fetchQuote(code.trim())} disabled={checking || !code.trim()} data-testid="payment-coupon-apply"
            className="rounded-full border border-[#144F75] text-[#144F75] px-4 py-2 text-xs font-medium hover:bg-[#144F75]/[0.06] disabled:opacity-50">
            {checking ? <Loader2 className="animate-spin inline" size={13} /> : "Apply"}
          </button>
        </div>
      )}

      <div className="mt-5 space-y-3">
        <button onClick={() => go("deposit", onDeposit)} disabled={!!busy} data-testid="pay-deposit-btn"
          className="w-full text-left rounded-2xl border-2 border-[#144F75] bg-[#144F75]/5 p-4 hover:bg-[#144F75]/10 transition-colors disabled:opacity-60">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-[#1A242B] flex items-center gap-2"><Wallet size={18} className="text-[#144F75]" /> Pay {pct}% deposit now</span>
            <span className="font-serif text-lg text-[#144F75]">{busy === "deposit" ? <Loader2 className="animate-spin" size={18} /> : `${money(deposit)}`}</span>
          </div>
          <p className="text-xs text-[#4A5D6B] mt-1">Remaining <strong>{money(balance)}</strong> due at your visit (cash or card).</p>
        </button>
        <button onClick={() => go("full", onFull)} disabled={!!busy} data-testid="pay-full-btn"
          className="w-full text-left rounded-2xl border border-[#E5E0D8] bg-white p-4 hover:border-[#144F75] transition-colors disabled:opacity-60">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-[#1A242B] flex items-center gap-2"><CreditCard size={18} className="text-[#4A5D6B]" /> Pay in full</span>
            <span className="font-serif text-lg text-[#1A242B]">{busy === "full" ? <Loader2 className="animate-spin" size={18} /> : `${money(price)}`}</span>
          </div>
          <p className="text-xs text-[#4A5D6B] mt-1">Settle the whole amount today — nothing due later.</p>
        </button>
      </div>
    </div>
  );
}
